import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { apiClient } from '@/core/api/apiClient'
import type { MediaGalleryItem } from '@/views/SuperAdmin/Management/TransporterManagement/components/dialogs/MediaGalleryDialog'

export const legPhotosStatusQueryKey = (planId: string) => ['leg_photos_status', planId] as const

export function useLegPhotosStatusForPlans(planIds: string[], enabled: boolean) {
  const uniquePlanIds = useMemo(
    () => [...new Set(planIds.filter(Boolean))].sort(),
    [planIds]
  )

  const results = useQueries({
    queries: uniquePlanIds.map((planId) => ({
      queryKey: legPhotosStatusQueryKey(planId),
      queryFn: () => apiClient.portal.admin.getLegPhotosStatus(planId),
      enabled: enabled && Boolean(planId),
      staleTime: 5 * 60 * 1000,
    })),
  })

  return useMemo(() => {
    const map = new Map<string, Record<number, { hasPreDelivery: boolean; hasPostDelivery: boolean }>>()
    uniquePlanIds.forEach((planId, index) => {
      map.set(planId, results[index]?.data?.statusByLeg ?? {})
    })
    return map
  }, [uniquePlanIds, results])
}

export async function fetchLegPhotosForGallery(
  planId: string,
  legIndex: number,
  flow: 'preDelivery' | 'postDelivery'
): Promise<{ photos: MediaGalleryItem[]; videos: MediaGalleryItem[] }> {
  const { photos: rawPhotos } = await apiClient.portal.admin.listLegPhotos(planId, legIndex, flow)
  const photos: MediaGalleryItem[] = []
  const videos: MediaGalleryItem[] = []

  for (const item of rawPhotos ?? []) {
    if (!item.storagePath && !item.url) continue
    const galleryItem: MediaGalleryItem = {
      key: item.id,
      storagePath: item.storagePath ?? '',
      prefetchedUrl: item.url,
      mediaKind: item.mediaType === 'video' ? 'video' : 'photo',
      noteText: item.angle,
      metaText: flow === 'preDelivery' ? 'Pre-delivery' : 'Post-delivery',
    }
    if (item.mediaType === 'video') videos.push(galleryItem)
    else photos.push(galleryItem)
  }

  return { photos, videos }
}
